import React from 'react';
import PropTypes from 'prop-types';
import { Alert } from 'antd';

/**
 * Modal Header Component
 *
 * Standardized modal header with title, subtitle, actions, and optional alert
 *
 * @example
 * <Modal.Header
 *   title="Edit Sales Order"
 *   subtitle="SO-12345"
 *   alert={{ message: "Warning message", type: "warning" }}
 *   actions={<Switch />}
 * />
 */
const ModalHeader = ({
    title,
    subtitle,
    actions,
    alert,
    children,
    className = '',
    ...props
}) => {
    // If children provided, use custom header
    if (children) {
        return (
            <div className={`ds-modal-header ${className}`} {...props}>
                {children}
            </div>
        );
    }

    return (
        <div className={`ds-modal-header ${className}`} {...props}>
            <div className="ds-modal-header__content">
                <div className="ds-modal-header__title-section">
                    {title && (
                        <div className="ds-modal-header__title">
                            {title}
                            {subtitle && (
                                <span className="ds-modal-header__subtitle">{subtitle}</span>
                            )}
                        </div>
                    )}
                </div>
                {actions && (
                    <div className="ds-modal-header__actions">
                        {actions}
                    </div>
                )}
            </div>
            {alert && (
                <Alert
                    message={alert.message}
                    type={alert.type || 'warning'}
                    showIcon={alert.showIcon !== false}
                    size="small"
                    style={{ marginTop: '12px', fontSize: '12px', ...alert.style }}
                />
            )}
        </div>
    );
};

ModalHeader.propTypes = {
    /** Header title (20px, bold) */
    title: PropTypes.node,

    /** Subtitle text (13px, gray) */
    subtitle: PropTypes.node,

    /** Right-side actions (toggles, buttons, etc.) */
    actions: PropTypes.node,

    /** Alert configuration { message, type, showIcon, style } */
    alert: PropTypes.shape({
        message: PropTypes.node.isRequired,
        type: PropTypes.oneOf(['success', 'info', 'warning', 'error']),
        showIcon: PropTypes.bool,
        style: PropTypes.object
    }),

    /** Custom header content (overrides title/subtitle/actions) */
    children: PropTypes.node,

    /** Additional CSS classes */
    className: PropTypes.string
};

export default ModalHeader;
